import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import policies from "@/data/policies.json";

const META = {
  "about": { title: "About Us", desc: "Who runs Nivesh Street and what we stand for." },
  "privacy-policy": { title: "Privacy Policy", desc: "How Nivesh Street collects, uses and protects your data." },
  "terms-of-use": { title: "Terms of Use", desc: "The rules for using niveshstreet.in and our services." },
  "editorial-policy": { title: "Editorial Policy", desc: "How we produce, verify and label our market content." },
  "financial-disclaimer": { title: "Financial Disclaimer", desc: "Important disclaimer about financial content on Nivesh Street." },
  "market-data-sources": { title: "Market Data & Sources", desc: "Where our market numbers come from and how fresh they are." },
  "advertising-sponsorship": { title: "Advertising & Sponsorship", desc: "How Nivesh Street handles ads, sponsored posts and partnerships." },
  "corrections-feedback": { title: "Corrections & Feedback", desc: "Spot an error or have feedback? Here's how to reach the newsroom." },
  "contact-us": { title: "Contact Us", desc: "Ways to reach Nivesh Street for support, press and partnerships." },
};

export default function PolicyPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const blocks = policies[slug];
  const meta = META[slug] || {};

  if (!blocks) {
    return (
      <div className="px-4 sm:px-6 lg:px-10 py-14 max-w-[760px] mx-auto text-slate-600">
        <h1 className="font-heading text-3xl font-extrabold text-[#1A1A1A] mb-3">Page not found</h1>
        <p className="mb-4">The policy you requested does not exist.</p>
        <Link to="/" className="text-[#D91E2A] font-semibold">← Back home</Link>
      </div>
    );
  }

  const title = blocks[0]?.text || meta.title;

  return (
    <article className="px-4 sm:px-6 lg:px-10 py-10 max-w-[780px] mx-auto" data-testid={`policy-${slug}`}>
      <Helmet>
        <title>{title} · Nivesh Street</title>
        <meta name="description" content={meta.desc || title} />
        <meta property="og:title" content={`${title} · Nivesh Street`} />
        <meta property="og:description" content={meta.desc || title} />
        <meta property="og:url" content={`https://niveshstreet.in/${slug}`} />
        <meta property="og:image" content="https://niveshstreet.in/og-social-card.jpg" />
        <link rel="canonical" href={`https://niveshstreet.in/${slug}`} />
      </Helmet>

      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-xs font-mono-fin uppercase tracking-widest text-[#8C8D90] hover:text-[#D91E2A] mb-5"
        data-testid="policy-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to home
      </Link>

      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold leading-[1.08] tracking-tight text-[#1A1A1A] mb-2">
        {title}
      </h1>
      <div className="h-[2px] w-24 bg-[#D91E2A] mb-7" />

      <div className="prose prose-slate max-w-none">
        {blocks.slice(1).map((b, i) => {
          if (b.type === "heading") {
            return (
              <h2 key={i} className="font-heading text-xl sm:text-2xl font-bold text-[#1A1A1A] mt-9 mb-2">
                {b.text}
              </h2>
            );
          }
          if (b.type === "meta") {
            return (
              <p key={i} className="text-sm italic text-[#6B6C6F] mb-1">{b.text}</p>
            );
          }
          return (
            <p key={i} className="text-[15px] sm:text-base leading-relaxed text-[#333] mb-4">
              {b.text}
            </p>
          );
        })}
      </div>
    </article>
  );
}
